'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Spinner } from '#pplx/frontend/aether/components/Spinner/index.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { LegacyTablerIconSize } from '#pplx/frontend/aether/primitives/IconPrimitive/iconSizing.js';
import { IconPrimitive } from '#pplx/frontend/aether/primitives/IconPrimitive/index.js';
import { Size as ButtonSize } from '#pplx/frontend/components/types/types.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { Link } from '#pplx/frontend/router/Link.js';
import classNames from 'classnames';
import { noop } from 'lodash-es';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
const CLICK_FEEDBACK_DURATION = 2000;
const CLICK_DEBOUNCE_DURATION = 1000;
const BUTTON_SIZE_STYLES = {
    [ButtonSize.tiny]: {
        height: 'h-6',
        text: 'text-xs',
        padding: 'px-2',
        iconWrapperSize: 'size-3.5',
        baselineShift: '-mb-px',
    },
    [ButtonSize.small]: {
        height: 'h-8',
        text: 'text-sm',
        padding: 'px-2.5',
        iconWrapperSize: 'size-4',
        baselineShift: '-mb-px',
    },
    [ButtonSize.regular]: {
        height: 'h-10',
        text: 'text-base',
        padding: 'px-3',
        iconWrapperSize: 'size-5',
        baselineShift: 'mb-[-2px]',
    },
    [ButtonSize.large]: {
        height: 'h-14',
        text: 'text-lg',
        padding: 'px-5',
        iconWrapperSize: 'size-7',
        baselineShift: 'mb-[-3px]',
    },
};
const getButtonSizeStyle = (size) => {
    return (BUTTON_SIZE_STYLES[size] ||
        BUTTON_SIZE_STYLES[ButtonSize.regular]);
};
const BUTTON_SIZE_TO_ICON_SIZE = {
    [ButtonSize.tiny]: LegacyTablerIconSize.xs,
    [ButtonSize.small]: LegacyTablerIconSize.sm,
    [ButtonSize.regular]: LegacyTablerIconSize.base,
    [ButtonSize.large]: LegacyTablerIconSize.lg,
    [ButtonSize.xl]: LegacyTablerIconSize.xl,
    [ButtonSize.none]: LegacyTablerIconSize.base,
};
const IconWrapper = ({ children, buttonSize = ButtonSize.regular }) => {
    return (_jsx("div", { className: classNames('flex shrink-0 items-center justify-center', getButtonSizeStyle(buttonSize).iconWrapperSize), children: children }));
};
const ButtonBase = memo(function ButtonBase(props) {
    const { text, textClassName = '', size: buttonSize = ButtonSize.regular, disabled = false, fullWidth, fullWidthMobile, layout = 'center', type = 'button', noWrap = true, onClick = noop, onMouseDown, href, target, extraCSS, icon, iconClassName, pill = false, noRounded = false, toolTip, tooltipLayout = 'top', tooltipProps, badge, chevron, isLoading, loadingVariant = 'spin', leadingComponent, trailingComponent, maxWidth, chevronIcon = $icon('chevron-down'), clickFeedback = false, debounce = false, testId, ariaLabel, variant, autoFocus, noPadding = false, ariaCurrent, ref, ...rest } = props;
    const [showClicked, setShowClicked] = useState(false);
    const [hasClicked, setHasClicked] = useState(false);
    const [disabledInternal, setDisabledInternal] = useState(disabled);
    // Memoize the button size styles
    const buttonSizeStyle = useMemo(() => getButtonSizeStyle(buttonSize), [buttonSize]);
    // Update the disabled state when the prop or we've clicked
    useEffect(() => {
        setDisabledInternal(debounce ? hasClicked || disabled : disabled);
    }, [debounce, hasClicked, disabled]);
    const handleClick = (e) => {
        // Prevent double clicks by optionally debouncing the click event
        if (!debounce || !hasClicked) {
            setHasClicked(true);
            setTimeout(() => setHasClicked(false), CLICK_DEBOUNCE_DURATION);
            onClick(e);
        }
        // Optionally flip to a ✔︎ icon when clicked and the prop is selected
        if (clickFeedback) {
            setShowClicked(true);
            setTimeout(() => setShowClicked(false), CLICK_FEEDBACK_DURATION);
        }
    };
    const hasText = !!text;
    const buttonClass = useMemo(() => classNames(extraCSS, 'font-sans focus:outline-hidden outline-hidden outline-transparent transition duration-300 ease-out select-none items-center relative group/button font-medium', {
        // Layout
        'justify-center text-center items-center': layout === 'center',
        'justify-start': layout === 'left',
        // Shape/Corners
        'rounded-none': noRounded,
        'rounded-lg': !pill && !noRounded,
        'rounded-full': pill && !noRounded,
        // Interaction states
        'cursor-default opacity-50': disabledInternal,
        'cursor-pointer': !disabledInternal,
        // Text wrapping
        'whitespace-nowrap': noWrap,
        'break-words': !noWrap,
        // Width behavior
        'flex w-full': fullWidth,
        'inline-flex': !fullWidth && !fullWidthMobile,
        'flex w-full md:inline-flex md:w-auto': fullWidthMobile,
        // Size
        [buttonSizeStyle.text]: true,
        [buttonSizeStyle.height]: true,
        // Padding
        [buttonSizeStyle.padding]: hasText,
        // Icon only
        [pill ? 'aspect-square' : 'aspect-[9/8]']: !hasText && !chevron && !badge && !(icon && leadingComponent),
    }), [
        badge,
        buttonSizeStyle,
        chevron,
        disabledInternal,
        extraCSS,
        fullWidth,
        fullWidthMobile,
        hasText,
        icon,
        layout,
        leadingComponent,
        noRounded,
        noWrap,
        pill,
    ]);
    const textIconGroup = useMemo(() => classNames('flex items-center min-w-0 gap-two', {
        'flex-col': layout === 'stacked',
        'justify-center': layout === 'center',
        'justify-between': layout === 'split',
        'w-full': fullWidth,
        'gap-2': layout === 'stacked',
    }), [fullWidth, layout]);
    const loaderSize = useMemo(() => {
        if (layout === 'stacked')
            return 16;
        if (buttonSize === ButtonSize.regular && !hasText)
            return 16;
        if (buttonSize === ButtonSize.large && hasText)
            return 16;
        return 14;
    }, [layout, buttonSize, hasText]);
    const buttonContent = (_jsxs("div", { className: textIconGroup, style: {
            maxWidth: maxWidth,
        }, children: [leadingComponent && _jsx("div", { children: leadingComponent }), isLoading && (_jsx(IconWrapper, { buttonSize: buttonSize, children: loadingVariant === 'spin' ? (_jsx(Spinner, { size: loaderSize })) : loadingVariant === 'pulse' ? (_jsx("div", { className: classNames('relative flex items-center justify-center', variant === 'primary' ? 'text-inverse' : 'text-super-primary'), children: _jsxs("div", { className: "relative scale-[0.8]", children: [_jsx(IconPrimitive, { icon: $icon('circle-filled'), size: BUTTON_SIZE_TO_ICON_SIZE[buttonSize] }), _jsx(IconPrimitive, { icon: $icon('circle-filled'), size: BUTTON_SIZE_TO_ICON_SIZE[buttonSize], className: "absolute inset-0 animate-ping" })] }) })) : null })), !isLoading && icon && (_jsx(IconWrapper, { buttonSize: buttonSize, children: _jsx(IconPrimitive, { icon: showClicked ? $icon('check') : icon, size: BUTTON_SIZE_TO_ICON_SIZE[buttonSize], className: iconClassName }) })), text && (_jsx("div", { className: classNames('relative truncate text-center mb-0', {
                    'px-1': !noPadding,
                    'leading-loose': layout !== 'stacked',
                    'leading-none': layout === 'stacked',
                }, buttonSizeStyle.baselineShift, textClassName), children: text })), badge && (_jsx("span", { children: _jsx(FormattedMessage, { defaultMessage: " \u00B7 {badge}", description: "Badge text with middle dot separator in a button", id: "c914931f87", values: { badge } }) })), chevron && (_jsx(IconPrimitive, { size: LegacyTablerIconSize.xs, icon: chevronIcon, className: "shrink-0 opacity-50" })), trailingComponent] }));
    // Attempt to derive an ariaLabel from the tooltip for an icon button
    // without text
    let dynamicAriaLabel;
    if (icon && !text && toolTip) {
        dynamicAriaLabel = toolTip;
    }
    const style = useMemo(() => ({ WebkitTapHighlightColor: 'transparent' }), []);
    let wrapper;
    if (href !== undefined) {
        wrapper = (_jsx(Link, { "data-testid": testId, role: "button", "aria-label": ariaLabel ?? dynamicAriaLabel, "aria-current": ariaCurrent, href: href, className: buttonClass, target: target, onClick: onClick, onMouseDown: onMouseDown, style: style, ...rest, ref: ref, children: buttonContent }));
    }
    else {
        wrapper = (_jsx("button", { "data-testid": testId, "aria-label": ariaLabel ?? dynamicAriaLabel, type: type, disabled: disabledInternal, onClick: handleClick, onMouseDown: onMouseDown, className: buttonClass, ...rest, ref: ref, children: buttonContent }));
    }
    if (toolTip) {
        return (_jsx(Tooltip, { content: toolTip, side: tooltipLayout, ...tooltipProps, children: wrapper }));
    }
    return wrapper;
});
export default ButtonBase;
//# sourceMappingURL=ButtonBase.js.map